import { createContext, Fragment, PropsWithChildren, useCallback, useContext, useState } from "react";
import { api } from "../../contracts";
import { useCourse } from "../course";
import { useModal } from "../modal";
import { useToast } from "../toast";
import DialogScoreExam from "../../components/DialogScoreExam";
import Button from "../../components/Button";
import { useHistory } from "react-router-dom";
import { URI } from "../../contracts/enums";
import { useMe } from "../me";
import { useAuth } from "../auth";
import { isNumber } from "lodash";
import { Exam, ExamAnswerResults, SendExamAnswer } from "../../contracts/exam/dto";

type OverloadSelect = {
    (examId: number): void,
    (exam: Exam): void
}

type ContextExamData = {
    items: Exam[];
    find: () => Promise<Exam[]>
    current: Exam | undefined,
    select: OverloadSelect,
    sendAnswer: (answers: string[]) => Promise<ExamAnswerResults>,
    getResults: (examId: number) => Promise<ExamAnswerResults>
}

const DEFAULT: ContextExamData = {
    items: [],
    find: () => { return Promise.resolve([]) },
    current: undefined,
    select: (param: number | Exam) => { },
    sendAnswer: (answers: string[]) => { return Promise.resolve({} as ExamAnswerResults) },
    getResults: () => { return Promise.resolve({} as ExamAnswerResults) }
}

const LongExamContext = createContext<ContextExamData>(DEFAULT);

export const LongExamProvider = ({ children }: PropsWithChildren<any>) => {
    const { type } = useCourse();
    const { addToast } = useToast();
    const { show, hide } = useModal();
    const history = useHistory();
    const { getMe } = useMe();
    const { updateUser } = useAuth();

    const [items, setItems] = useState<Exam[]>([]);
    const [selected, setSelected] = useState<Exam>();

    const find = useCallback(async () => {
        const response = await api.longExam.getList(type);
        setItems(response);
        setSelected(undefined);
        return response;
    }, [type]);

    const getResults = useCallback(async (examId: number) => {
        return await api.longExam.get(type, examId);
    }, [type]);

    const select = useCallback(async (param: number | Exam) => {
        let exam: Exam | undefined;
        if (isNumber(param)) {
            const examId = param;
            exam = items.find(e => e.id === examId);
        } else {
            exam = param;
        }
        if (!!exam) {
            setSelected(exam);
            if (exam.is_answered && !exam.results) {
                exam.results = await getResults(exam.id);
            }
        }
    }, [getResults, items]);

    const gotoExamResults = useCallback(() => {
        hide();
        history.push(URI.longExamResults, { payload: selected });
    }, [hide, history, selected]);

    const sendAnswer = useCallback(async (answers: string[]): Promise<ExamAnswerResults> => {
        if (!selected) {
            addToast({
                type: 'error',
                title: 'Nenhuma prova foi selecionada para ser respondida'
            });
            throw new Error('No exam was select');
        }
        const questAnswers: Record<string, string> = {};
        answers.forEach((a, index) => {
            questAnswers[`question_${index + 1}`] = a;

        })

        const payload: SendExamAnswer = {
            examId: selected.id,
            answers: questAnswers
        }
        const response = await api.longExam.sendAnswers(type, payload);
        selected.results = response;
        const userMe = await getMe();
        updateUser(userMe);

        show({
            header: <Fragment>{selected.name} Concluída!</Fragment>,
            content: <DialogScoreExam answersExam={response} />,
            footer: <Button onClick={() => gotoExamResults()} value="Ver seu Gabarito" />
        })

        return response;
    }, [addToast, getMe, gotoExamResults, selected, show, type, updateUser]);


    return (
        <LongExamContext.Provider
            value={{
                items,
                find,
                current: selected,
                select,
                sendAnswer,
                getResults
            }}>
            {children}
        </LongExamContext.Provider>
    )
}

export const useLongExam = (): ContextExamData => {
    const context = useContext(LongExamContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}